// Layer 3 (Transformation): polish.P9 (#657) — the 4-column stage panel beside
// the flowsheet (locked comp `metal-accounting.html` A, `stagePanel`). It
// replaces the old node-detail section under the page: the strip cell or the
// graph node picks a stage, and this read lays out that stage's balance as
// hairline facts. Every value is an existing engine read (`closureRead`,
// `lossDrillRead`, `tailsLossRead`, `massBalanceRows`, the MA layer's
// in-circuit cell); nothing is re-derived. Pure — no React.

import type { SamplePoint } from '@/versions/v1/schema/unit-operation';
import type { ClosureRead, MeasuredQuantity } from '@/versions/v1/schema/metal-accounting';
import { CASES } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { ladderLineForCase } from '@/versions/v1/shared/ladder.transform';
import { DEFAULT_CURSOR } from '@/versions/v1/schema/reading-series';
import type { VizTone } from '@/versions/v1/modules/_viz';
import { adherenceTone } from '@/versions/v1/modules/ingestion/ProcessGraph.transform';
import { nodeHumanName } from '@/versions/v1/modules/ingestion/Scene.transform';
import {
  closureRead,
  formatGapPct,
  formatMeasuredQuantity,
  lossDrillRead,
  massBalanceRows,
  metalAccountingLayer,
  tailsLossRead,
  type PeriodView,
} from '@/versions/v1/modules/ingestion/MetalAccountingLayer.transform';
import { headGrade, STAGE_NODE, type BalanceStageId } from './BalanceStrip.transform';

/** One run of a fact's muted line; `block` starts a line of its own. */
export type PanelRun = { text: string; tone?: VizTone; block?: boolean };

export type PanelFact = {
  id: string;
  label: string;
  value: string;
  valueTone?: VizTone;
  sub: readonly PanelRun[];
  /** A case the fact opens in the case drawer (the stage's open recovery case,
   *  or WIT-021's gross-error case). */
  action?: { caseId: string; label: string; drawerTitle: string; tone: VizTone };
  /** The stage row that carries an open case takes the state rule. */
  flagged?: boolean;
};

export type PanelSection = {
  id: string;
  label: string | null;
  basis: string | null;
  facts: readonly PanelFact[];
};

export type StagePanelView = {
  stage: BalanceStageId;
  title: string;
  /** Node the "Open in Advisory" link scopes to; `null` for the plant-wide closure. */
  advisoryNodeId: string | null;
  sections: readonly PanelSection[];
};

const DASH = '—';

/** A measurement name read mid-line: `Concentrate mass` → `concentrate mass`,
 *  but an element symbol keeps its case (`Zn grade`). */
function sentenceTail(text: string): string {
  return /^[A-Z][a-z]/.test(text) && !/^(Zn|Pb|Ag|Cu|Fe)\b/.test(text) ? text[0]!.toLowerCase() + text.slice(1) : text;
}

function display(quantity: MeasuredQuantity, view: PeriodView): string {
  if (quantity.value === null || quantity.unit === 'unmeasured') return DASH;
  return formatMeasuredQuantity(quantity, view.period);
}

function gapTone(read: ClosureRead): VizTone | undefined {
  if (read.closurePct === null || read.tone === 'unmeasured') return undefined;
  return read.tone;
}

function balanceFacts(read: ClosureRead, view: PeriodView): PanelFact[] {
  return [
    { id: 'in', label: 'In', value: display(read.in, view), sub: [] },
    { id: 'out', label: 'Out', value: display(read.out, view), sub: [] },
    { id: 'inventory', label: 'Δ inventory', value: display(read.inventoryChange, view), sub: [] },
    {
      id: 'gap',
      label: 'Gap',
      value: read.closurePct !== null ? formatGapPct(read.closurePct) : DASH,
      valueTone: gapTone(read),
      sub: [],
    },
  ];
}

function assayFacts(read: ClosureRead): PanelFact[] {
  return read.measurements.map((row) => ({
    id: row.id,
    label: `${row.label} · ${sentenceTail(row.measurement)}`,
    value: formatMeasuredQuantity({ value: row.value, unit: row.unit }),
    sub: [{ text: `${row.source} · sampled` }],
  }));
}

function circuitSections(stage: 'pb' | 'zn', view: PeriodView, withTarp: boolean): PanelSection[] {
  const nodeId = STAGE_NODE[stage]!;
  const element = stage === 'pb' ? 'Pb' : 'Zn';
  const read = closureRead(nodeId, view);
  const drill = lossDrillRead(element, view);
  const sections: PanelSection[] = [{ id: 'balance', label: null, basis: null, facts: balanceFacts(read, view) }];
  const assays = assayFacts(read);
  if (assays.length > 0) sections.push({ id: 'assays', label: 'Assays', basis: null, facts: assays });
  sections.push({
    id: 'stages',
    label: 'Stages in this circuit',
    basis: drill.method,
    facts: drill.rows.map((row) => {
      const design =
        row.outcomeDesign.value !== null
          ? `vs design ${formatMeasuredQuantity(row.outcomeDesign, undefined, { decimals: 1 })}`
          : '';
      const sub: PanelRun[] = [{ text: row.caseId ? design : `${design}${design ? ' · ' : ''}no open case` }];
      if (row.caseId && withTarp) {
        const caseItem = CASES.find((entry) => entry.id === row.caseId);
        const ladder = caseItem ? ladderLineForCase(caseItem, DEFAULT_CURSOR) : null;
        if (ladder) sub.push({ text: ladder.text, tone: ladder.tone, block: true });
      }
      return {
        id: row.stageId,
        label: row.label,
        value: row.outcomeValue.value !== null ? formatMeasuredQuantity(row.outcomeValue, undefined, { decimals: 1 }) : DASH,
        sub,
        flagged: row.tone === 'bad',
        action:
          row.caseId && row.caseTitle
            ? { caseId: row.caseId, label: row.caseTitle, drawerTitle: row.caseTitle, tone: 'bad' }
            : undefined,
      };
    }),
  });
  return sections;
}

/** Feed metal in the period, the unit named per element (`93.57 dst Zn`,
 *  `760.2 oz Ag`) — the same `metalIn` the balance and the tails loss read. */
function feedMetal(element: 'Zn' | 'Pb' | 'Ag', view: PeriodView): string {
  const metal = tailsLossRead(element, view).metalInFeed;
  if (metal.value === null) return DASH;
  const unit = element === 'Ag' ? 'oz Ag' : `dst ${element}`;
  return formatMeasuredQuantity({ value: metal.value, unit }, undefined, { decimals: element === 'Ag' ? 1 : 2 });
}

function feedSections(view: PeriodView): PanelSection[] {
  const crush = closureRead('sp-crush', view);
  const grind = closureRead('sp-grind', view);
  const anchor = crush.measurements.find((row) => row.id === 'ms-wit021') ?? crush.measurements[0];
  const measured = view.period !== 'shift';
  const facts: PanelFact[] = [];
  if (anchor) {
    facts.push({
      id: 'wit021',
      label: `${anchor.label} · ${sentenceTail(anchor.measurement)}`,
      value: measured ? formatMeasuredQuantity({ value: anchor.value, unit: anchor.unit }) : DASH,
      sub: [{ text: `${anchor.source} · metered` }],
      action: anchor.grossErrorFlag
        ? {
            caseId: anchor.grossErrorFlag.caseId,
            label: anchor.grossErrorFlag.chipLabel,
            drawerTitle: anchor.grossErrorFlag.drawerTitle,
            tone: 'warn',
          }
        : undefined,
    });
  }
  facts.push({ id: 'grind-inventory', label: 'Grinding Δ inventory', value: display(grind.inventoryChange, view), sub: [] });
  return [
    { id: 'feed', label: null, basis: null, facts },
    {
      id: 'feed-metal',
      label: 'Feed metal',
      basis: null,
      // Each metal's feed sits over its head grade, back-calculated from the
      // three-product balance (the seed has no head-assay tag).
      facts: (['Zn', 'Pb', 'Ag'] as const).map((element) => ({
        id: `feed-${element}`,
        label: element,
        value: feedMetal(element, view),
        sub: measured ? [{ text: `${headGrade(element, view)} head · back-calculated` }] : [],
      })),
    },
  ];
}

function tailsSections(view: PeriodView, samplePoints: readonly SamplePoint[]): PanelSection[] {
  const tails = massBalanceRows(view).find((row) => row.id === 'mb-tails');
  const sa05 = samplePoints.find((row) => row.id === 'SA05');
  const sub: PanelRun[] = [{ text: 'SA05 · sampled' }];
  if (sa05 && sa05.adherence !== 'REG') {
    sub.push({ text: ' · ' }, { text: sa05.adherence.toLowerCase(), tone: adherenceTone(sa05.adherence) });
  }
  return [
    {
      id: 'tails',
      label: null,
      basis: null,
      facts: [{ id: 'final-tails', label: 'Final tailings', value: tails?.tonnes ?? DASH, sub }],
    },
    {
      id: 'loss',
      label: 'Lost to tails · of feed metal',
      basis: null,
      facts: (['Zn', 'Pb', 'Ag'] as const).map((element) => {
        const read = tailsLossRead(element, view);
        const assay = read.tailsAssay ? formatMeasuredQuantity(read.tailsAssay, undefined, { decimals: 2 }) : null;
        const word =
          read.tailsAssayTone === 'bad' ? 'exceeds threshold' : read.tailsAssayTone === 'ok' ? 'within threshold' : null;
        return {
          id: `loss-${element}`,
          label: element,
          value: read.lossPct !== null ? `${read.lossPct.toFixed(1)}%` : DASH,
          sub:
            assay && word
              ? [
                  { text: `${assay} tails`, tone: read.tailsAssayTone === 'bad' ? 'bad' : undefined },
                  { text: ` · ${word}` },
                ]
              : [],
        };
      }),
    },
  ];
}

function closureSections(view: PeriodView): PanelSection[] {
  const circuit = (nodeId: 'sp-pb' | 'sp-zn', label: string): PanelFact => {
    const read = closureRead(nodeId, view);
    const measured = read.closurePct !== null;
    return {
      id: nodeId,
      label,
      value: measured ? formatGapPct(read.closurePct!) : DASH,
      valueTone: gapTone(read),
      sub: measured ? [{ text: `in ${display(read.in, view)} · out ${display(read.out, view)}` }] : [],
    };
  };
  const inCircuit = metalAccountingLayer(view).strip.cells.find((cell) => cell.id === 'ma-in-circuit');
  const filter = closureRead('sp-filter', view);
  return [
    {
      id: 'closure',
      label: null,
      basis: null,
      facts: [
        circuit('sp-pb', 'Pb/Ag circuit'),
        circuit('sp-zn', 'Zn circuit'),
        {
          id: 'in-circuit',
          label: 'Metal in circuit',
          value: inCircuit && inCircuit.value !== DASH ? inCircuit.value : 'unmeasured',
          sub: inCircuit && inCircuit.value !== DASH ? [{ text: inCircuit.sub }] : [],
        },
        {
          id: 'filter-inventory',
          label: 'Concentrate thickening Δ inventory',
          value: display(filter.inventoryChange, view),
          sub: [],
        },
      ],
    },
  ];
}

const STAGE_TITLE: Readonly<Record<BalanceStageId, string>> = {
  feed: 'Ore receiving, crushing & grinding',
  pb: nodeHumanName('sp-pb'),
  zn: nodeHumanName('sp-zn'),
  tails: nodeHumanName('sp-paste'),
  closure: 'Two-path closure',
};

/** The stage panel for one strip stage. `withTarp` keeps the Superintendent's
 *  TARP ladder line (the old persona card's read) on the open case. */
export function stagePanelView(
  stage: BalanceStageId,
  view: PeriodView,
  samplePoints: readonly SamplePoint[],
  options: { withTarp: boolean },
): StagePanelView {
  const sections =
    stage === 'pb' || stage === 'zn'
      ? circuitSections(stage, view, options.withTarp)
      : stage === 'feed'
        ? feedSections(view)
        : stage === 'tails'
          ? tailsSections(view, samplePoints)
          : closureSections(view);
  return { stage, title: STAGE_TITLE[stage], advisoryNodeId: STAGE_NODE[stage], sections };
}
